// Клиент двух ручек ручного подтверждения слияния:
// `GET /api/v1/tasks/{id}/merge_gate` и `POST /api/v1/tasks/{id}/approve_merge`.
// Типы вшиты руками: генерации из OpenAPI во фронте нет.
//
// Отказы — `ApiError` (`code` + данные случая из `detail`); `422` по схеме
// тела — список `detail`, машиночитаемого кода нет.

import { apiErrorFromResponse } from "../../api/apiError";
import { authedFetch } from "../../auth/authedFetch";
import type { TaskResponse } from "../queue/api";

export type PullRequestState = "open" | "merged" | "closed";
export type GateCheckId =
  | "has_changes"
  | "ci_green"
  | "mergeable"
  | "target_branch"
  | "no_blocking_label"
  | "threads_resolved"
  | "acceptance_closed";
export type PredicateOutcome = "passed" | "failed" | "unknown";
export type PredicateClass = "prohibiting" | "acceptable" | "not_participating";

export interface MergeGatePredicate {
  readonly id: GateCheckId;
  readonly outcome: PredicateOutcome;
  readonly reason: string;
  readonly predicate_class: PredicateClass;
  readonly state_token: string | null;
}

export interface MergeGateResponse {
  readonly task_id: string;
  readonly pr_state: PullRequestState;
  readonly head_sha: string;
  readonly base_branch: string;
  readonly reviewed_head_sha: string | null;
  readonly open_acceptance_count: number;
  readonly predicates: readonly MergeGatePredicate[];
}

export interface AcknowledgedPredicate {
  readonly predicate: GateCheckId;
  readonly state_token: string;
}

export interface ApproveMergeRequest {
  readonly head_sha: string;
  readonly base_branch: string;
  readonly acknowledged: readonly AcknowledgedPredicate[];
}

// Коды ручек подтверждения слияния — `docs/spec/modules/task/http.md`;
// `permission_denied` — код ядра, разбирается отдельно.
export const REFUSAL_CODES = [
  "task_not_found",
  "task_cancelled",
  "not_awaiting_approval",
  "no_pull_request",
  "invalid_acknowledgement",
  "gate_unavailable",
  "host_auth",
  "pr_closed",
  "head_moved",
  "base_moved",
  "approval_stale",
  "gate_blocked",
  "merge_refused",
] as const;

export type RefusalCode = (typeof REFUSAL_CODES)[number];

export function isRefusalCode(value: string): value is RefusalCode {
  return (REFUSAL_CODES as readonly string[]).includes(value);
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function fetchMergeGate(
  taskId: string,
): Promise<MergeGateResponse> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}/merge_gate`, {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "mergeGate");
  }
  return (await response.json()) as MergeGateResponse;
}

export async function approveMerge(
  taskId: string,
  body: ApproveMergeRequest,
): Promise<TaskResponse> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}/approve_merge`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "approveMerge");
  }
  return (await response.json()) as TaskResponse;
}
